//#region Imports

import { Directive, effect, inject, Injector, OnDestroy } from '@angular/core';
import { RequestQueryBuilder, SCondition } from '@nestjsx/crud-request';
import { Joins } from './models/typies/joins.type';
import { Sorts } from './models/typies/sorts.type';
import { PaginationDto } from './models/interfaces/pagination-dto';
import { HttpConfigInterface } from './models/interfaces/http-config.interface';
import { ParamsService } from '../params/params.service';
import { Api } from '../api/api';
import { Subject, takeUntil } from 'rxjs';
import { ParamsConfig } from "../params/models/params.config";

//#endregion

@Directive()
export abstract class Pagination<TDto extends any> implements OnDestroy {

  public amountFilters: number = 0;

  public isLoading = true;

  private readonly cache: Map<string, { data: PaginationDto<TDto>; timestamp: number }> = new Map();

  private readonly destroy$ = new Subject<void>();

  protected source: PaginationDto<TDto> = {
    data: [],
    page: 1,
    pageCount: 0,
    count: 0,
    total: 0,
  };

  protected readonly paramsService = inject(ParamsService);

  protected readonly injector = inject(Injector);

  protected readonly api: Api = new Api();

  protected params: Partial<ParamsConfig> = {};

  protected route!: string;

  protected sort!: Sorts;

  protected size!: number;

  public ngOnDestroy(): void {
    this.api.abortAll();
    this.destroy$.next();
    this.destroy$.complete();
  }

  public async configPagination(httpConfig: HttpConfigInterface): Promise<void> {
    this.route = httpConfig.route;
    this.sort = httpConfig.sort || { field: 'id', order: 'DESC' };
    this.size = httpConfig.pageSize || 10;

    effect(() => {
      const params = this.paramsService.params();
      const page = this.source.page;

      this.getAmountFilters();

      this.params = params;
      this.sort = params.sort ? params.sort : this.sort;

      if (page === params.page && page !== 1)
        return this.reload();

      this.source.page = params.page || 1;

      this.loadPaginatedData(
        this.route,
        this.source.page || 1,
        this.size || 20,
      )
    }, { injector: this.injector });
  }

  public reload(): void {
    this.source.page = 1;

    this.paramsService.params = {
      page: undefined,
    };
  }

  public async onChangeSort(event?: Sorts): Promise<void> {
    if (!event) {
      this.paramsService.params = {
        sort: undefined
      };

      return;
    }

    this.paramsService.params = {
      sort: event,
    };
  }

  public async onChangePage(page: number): Promise<void> {
    this.paramsService.params = {
      page,
    };
  }

  private getAmountFilters(): void {
    this.amountFilters = this.paramsService.amount(['search', 'page', 'sort']);
  }

  private loadPaginatedData(url: string, page: number, limit: number): void {
    this.isLoading = true;

    this.destroy$.next();

    let query = new RequestQueryBuilder()
      .setPage(page)
      .setLimit(limit)
      .setOffset(0)
      .sortBy(this.sort)
      .search({});

    query = this.applySearch(query);
    query = this.applyJoin(query);

    const cacheKey = `${url}?page=${page}&limit=${limit}&params=${JSON.stringify(query.queryObject)}`;

    const cachedResponse = this.cache.get(cacheKey);
    const now = Date.now();

    if (cachedResponse && now - cachedResponse.timestamp < 60000) {
      this.source = cachedResponse.data;
      this.source.page = page;
      this.isLoading = false;
      return;
    }

    this.api.get<PaginationDto<TDto>>(url, {
      params: query.queryObject,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.source = response;
            this.cache.set(cacheKey, { data: response, timestamp: now });
          }
        },
        error: () => {
          this.isLoading = false;
        },
        complete: () => {
          this.isLoading = false;
        }
      });
  }

  private applySearch(query: RequestQueryBuilder): RequestQueryBuilder {
    const searchQuery = this.search();

    if (searchQuery)
      query = query.search({ ...searchQuery, });

    return query;
  }

  protected applyJoin(query: RequestQueryBuilder): RequestQueryBuilder {
    const customJoinParams = this.join();

    if (customJoinParams)
      return query.setJoin(customJoinParams);

    return query;
  }

  protected search(): SCondition | undefined {
    return undefined;
  }

  protected join(): Joins | undefined {
    return undefined;
  }

}
